import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import * as Icons from './Icons';
import { MasterHandSandbox } from './MasterHandSandbox';
import { useAuth } from '../contexts/AuthContext';
import { uploadMediaFile, resolveLocalUrl } from '../utils/mediaUtils';

const ALL_ICONS = [
  { key: 'LikeIcon', name: 'Like' },
  { key: 'BrokenHeartIcon', name: 'Broken Heart' },
  { key: 'CommentIcon', name: 'Comment' },
  { key: 'CheckmarkIcon', name: 'Checkmark / Vouch' },
  { key: 'BumpIcon', name: 'Bump / Repost' },
  { key: 'BookmarkIcon', name: 'Bookmark' },
  { key: 'ImageIcon', name: 'Image Attachment' },
  { key: 'VideoIcon', name: 'Video Attachment' },
  { key: 'AudioIcon', name: 'Audio Attachment' },
  { key: 'MicIcon', name: 'Microphone / Voice' },
  { key: 'CloseIcon', name: 'Close' },
  { key: 'PlayIcon', name: 'Play' },
  { key: 'PauseIcon', name: 'Pause' },
  { key: 'TrashIcon', name: 'Delete / Trash' },
  { key: 'CameraIcon', name: 'Camera' },
  { key: 'HomeIcon', name: 'Home Navigation' },
  { key: 'UserIcon', name: 'User Profile' },
  { key: 'UsersIcon', name: 'Community / Group' },
  { key: 'MailIcon', name: 'Direct Messages' },
  { key: 'SendIcon', name: 'Send Message' },
  { key: 'SettingsIcon', name: 'Settings' },
  { key: 'EditIcon', name: 'Edit Content' },
  { key: 'ExternalLinkIcon', name: 'External Link' },
  { key: 'ArrowUpIcon', name: 'Arrow Up' },
  { key: 'GalleryIcon', name: 'Gallery' },
  { key: 'PaperclipIcon', name: 'Attachment / Paperclip' },
  { key: 'ChevronLeftIcon', name: 'Chevron Left' },
  { key: 'ChevronRightIcon', name: 'Chevron Right' },
  { key: 'VolumeHighIcon', name: 'Volume High' },
  { key: 'VolumeOffIcon', name: 'Volume Off' },
  { key: 'DownloadIcon', name: 'Download' },
  { key: 'LogoutIcon', name: 'Logout' },
  { key: 'SearchIcon', name: 'Search' },
  { key: 'GoodieWhiteIcon', name: 'Goodie Mascot' },
  { key: 'ReplyIcon', name: 'Reply' },
  { key: 'ForwardIcon', name: 'Forward' },
  { key: 'AtIcon', name: 'Mention / At' },
  { key: 'EmojiIcon', name: 'Emoji' },
  { key: 'MoreHorizontalIcon', name: 'More Options' },
  { key: 'PlusIcon', name: 'Add / Plus' },
  { key: 'EyeIcon', name: 'Show Password / View' },
  { key: 'EyeOffIcon', name: 'Hide Password' },
  { key: 'TrophyIcon', name: 'Trophy / Streak' },
  { key: 'HashtagIcon', name: 'Hashtag' },
  { key: 'GlobeIcon', name: 'Globe / Public' },
  { key: 'FilterIcon', name: 'Filter' },
  { key: 'XIcon', name: 'Close / Cancel X' },
  { key: 'PrivateIcon', name: 'Lock / Private' },
  { key: 'ScreenShareIcon', name: 'Screen Share' },
  { key: 'FlipCameraIcon', name: 'Flip Camera' },
  { key: 'CopyIcon', name: 'Copy' },
  { key: 'PasteIcon', name: 'Paste' },
  { key: 'LockIcon', name: 'Lock' },
  { key: 'ShieldIcon', name: 'Shield / Safety' },
  { key: 'SparklesIcon', name: 'Sparkles / AI' },
  { key: 'FontIcon', name: 'Typography / Font' },
  { key: 'PostcardIcon', name: 'Postcard' },
  { key: 'FireIcon', name: 'Flame / Hot' },
  { key: 'FlagIcon', name: 'Flag / Report' },
  { key: 'BellIcon', name: 'Notification Bell' },
  { key: 'CalendarIcon', name: 'Calendar' },
  { key: 'ClockIcon', name: 'Clock / Duration' },
  { key: 'StoreIcon', name: 'Mod Store' },
  { key: 'RotateIcon', name: 'Refresh / Rotate' },
  { key: 'ExpandIcon', name: 'Fullscreen / Expand' }
];
const hexToRgba = (hex: string, op: number) => {
  let c = hex.replace('#', '');
  if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  const r = parseInt(c.substring(0, 2), 16), g = parseInt(c.substring(2, 4), 16), b = parseInt(c.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${op / 100})`;
};

interface MasterHandProps {
  onBack: () => void;
}

interface ComponentItem {
  id: string;
  name: string;
  filename: string;
  description: string;
  code: string;
  draft: string | null;
  draftUpdatedAt: string | null;
}

export const MasterHand = ({ onBack }: MasterHandProps) => {
  const { currentUser, usersData, updateUserData, isDemo } = useAuth();
  const [activeTab, setActiveTab] = useState<'visual' | 'code' | 'icons'>('visual');
  const [components, setComponents] = useState<ComponentItem[]>([]);
  const [selectedCompId, setSelectedCompId] = useState<string>('todo');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mobile layout responsiveness states
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Code Editor states
  const [editorCode, setEditorCode] = useState<string>('');
  const [sandboxCode, setSandboxCode] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [savingSandbox, setSavingSandbox] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [compileStatus, setCompileStatus] = useState<'idle' | 'compiling' | 'success' | 'error'>('idle');
  const [compileErrorMessage, setCompileErrorMessage] = useState<string | null>(null);

  // Visual Layout states
  const [activeMods, setActiveMods] = useState<string[]>([]);
  const [selectedLayoutMod, setSelectedLayoutMod] = useState<string | null>(null);
  const [layoutThemeColor, setLayoutThemeColor] = useState<string>('#8b5cf6');
  const [savingLayout, setSavingLayout] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // System Icons states
  const [selectedIconKey, setSelectedIconKey] = useState<string>('HomeIcon');
  const [iconSearch, setIconSearch] = useState<string>('');
  const [iconOverrideType, setIconOverrideType] = useState<'default' | 'text' | 'media' | 'svg'>('default');
  const [iconText, setIconText] = useState<string>('');
  const [iconMediaUrl, setIconMediaUrl] = useState<string>('');
  const [iconSvgContent, setIconSvgContent] = useState<string>('');
  const [iconSvgViewBox, setIconSvgViewBox] = useState<string>('0 0 24 24');
  const [iconSvgFill, setIconSvgFill] = useState<string>('none');
  const [iconSvgStroke, setIconSvgStroke] = useState<string>('currentColor');
  const [iconSvgStrokeWidth, setIconSvgStrokeWidth] = useState<string>('2');
  const [uploadingIconMedia, setUploadingIconMedia] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [savingIcon, setSavingIcon] = useState<boolean>(false);

  // References for synchronized scrolling in code editor
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Sync scroll between textarea and line numbers
  const handleScroll = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  // Fetch components and drafts
  const fetchComponents = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/admin/master-hand/components', {
        headers: { 'x-admin-username': currentUser || '' }
      });
      if (res.data.success) {
        setComponents(res.data.components);

        // Load initial code for the selected component
        const initial = res.data.components.find((c: ComponentItem) => c.id === selectedCompId);
        if (initial) {
          const currentCode = initial.draft || initial.code || '';
          setEditorCode(currentCode);
          setSandboxCode(currentCode);
          setHasChanges(!!initial.draft);
        }
      } else {
        setError(res.data.error || 'Failed to fetch components.');
      }
    } catch (err: any) {
      console.error('[MasterHand] fetch error:', err);
      setError(err.response?.data?.error || 'Failed to reach API server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComponents();

    // Set initial active mods for layout visual builder
    if (currentUser && usersData[currentUser]) {
      const mods = usersData[currentUser].activeCustomizations?.mods || [];
      setActiveMods(mods);
    }
  }, [currentUser]);

  // Handle selected component change
  useEffect(() => {
    const comp = components.find((c) => c.id === selectedCompId);
    if (comp) {
      const currentCode = comp.draft || comp.code || '';
      setEditorCode(currentCode);
      setSandboxCode(currentCode);
      setHasChanges(!!comp.draft);
      setCompileStatus('idle');
      setCompileErrorMessage(null);
    }
  }, [selectedCompId, components]);

  // Load selected icon's config
  useEffect(() => {
    const systemData = usersData?.['goodlyfe'] || {};
    const appIcons = systemData.appIcons || {};
    const config = appIcons[selectedIconKey];

    if (config) {
      setIconOverrideType(config.type || 'default');
      setIconText(config.text || '');
      setIconMediaUrl(resolveLocalUrl(config.url || ''));
      setIconSvgContent(config.svgContent || '');
      setIconSvgViewBox(config.viewBox || '0 0 24 24');
      setIconSvgFill(config.fill || 'none');
      setIconSvgStroke(config.stroke || 'currentColor');
      setIconSvgStrokeWidth(config.strokeWidth || '2');
    } else {
      setIconOverrideType('default');
      setIconText('');
      setIconMediaUrl('');
      setIconSvgContent('');
      setIconSvgViewBox('0 0 24 24');
      setIconSvgFill('none');
      setIconSvgStroke('currentColor');
      setIconSvgStrokeWidth('2');
    }
  }, [selectedIconKey, usersData]);

  const handleIconMediaUpload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      showToast('File size must be under 5MB', 'error');
      return;
    }
    try {
      setUploadingIconMedia(true);
      setUploadProgress(0);
      const url = await uploadMediaFile(file, 'goodlyfe', (progress) => {
        setUploadProgress(Math.round(progress));
      });
      setIconMediaUrl(url);
      setIconOverrideType('media');
      showToast('Media uploaded successfully!', 'success');
    } catch (err: any) {
      console.error('[IconUpload] error:', err);
      showToast('Upload failed. Please try again.', 'error');
    } finally {
      setUploadingIconMedia(false);
      setUploadProgress(0);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleIconMediaUpload(file);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const file = e.clipboardData.files?.[0];
    if (file) {
      e.preventDefault();
      handleIconMediaUpload(file);
    }
  };

  const handleClipboardButtonPaste = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        showToast('Clipboard reading not supported in this browser', 'error');
        return;
      }
      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        const imageTypes = clipboardItem.types.filter(type => type.startsWith('image/'));
        if (imageTypes.length > 0) {
          const blob = await clipboardItem.getType(imageTypes[0]);
          const file = new File([blob], 'clipboard-image.png', { type: imageTypes[0] });
          handleIconMediaUpload(file);
          return;
        }
      }
      showToast('No image found in clipboard', 'error');
    } catch (err) {
      console.error('Clipboard paste error:', err);
      showToast('Clipboard access denied', 'error');
    }
  };

  const handleSaveIconOverride = async () => {
    if (!currentUser) return;
    try {
      setSavingIcon(true);
      const systemData = usersData?.['goodlyfe'] || {};
      const currentIcons = systemData.appIcons || {};

      let updatedConfig: any = null;

      if (iconOverrideType === 'text') {
        if (!iconText || iconText.trim() === '') {
          showToast('Please enter text/emoji', 'error');
          return;
        }
        updatedConfig = {
          type: 'text',
          text: iconText.substring(0, 4)
        };
      } else if (iconOverrideType === 'media') {
        if (!iconMediaUrl) {
          showToast('Please upload or paste a media file first', 'error');
          return;
        }
        updatedConfig = {
          type: 'media',
          url: iconMediaUrl
        };
      } else if (iconOverrideType === 'svg') {
        if (!iconSvgContent || iconSvgContent.trim() === '') {
          showToast('Please enter SVG content', 'error');
          return;
        }
        updatedConfig = {
          type: 'svg',
          svgContent: iconSvgContent,
          viewBox: iconSvgViewBox,
          fill: iconSvgFill,
          stroke: iconSvgStroke,
          strokeWidth: iconSvgStrokeWidth
        };
      }

      const nextIcons = { ...currentIcons };
      if (updatedConfig) {
        nextIcons[selectedIconKey] = updatedConfig;
      } else {
        delete nextIcons[selectedIconKey];
      }

      // Update reactive local state for system user
      updateUserData((prev: any) => ({
        ...prev,
        goodlyfe: {
          ...(prev.goodlyfe || {}),
          appIcons: nextIcons
        }
      }));

      // Persist in DB
      if (!isDemo) {
        await api.put('/users/goodlyfe', {
          appIcons: nextIcons
        });
      }

      showToast(`${selectedIconKey} customized successfully!`, 'success');
    } catch (err: any) {
      console.error('[SaveIconOverride] error:', err);
      showToast('Failed to save icon override.', 'error');
    } finally {
      setSavingIcon(false);
    }
  };

  const handleResetIconToDefault = async () => {
    if (!currentUser) return;
    try {
      setSavingIcon(true);
      const systemData = usersData?.['goodlyfe'] || {};
      const currentIcons = systemData.appIcons || {};

      const nextIcons = { ...currentIcons };
      delete nextIcons[selectedIconKey];

      // Update reactive local state for system user
      updateUserData((prev: any) => ({
        ...prev,
        goodlyfe: {
          ...(prev.goodlyfe || {}),
          appIcons: nextIcons
        }
      }));

      // Persist in DB
      if (!isDemo) {
        await api.put('/users/goodlyfe', {
          appIcons: nextIcons
        });
      }

      // Reset editor states
      setIconOverrideType('default');
      setIconText('');
      setIconMediaUrl('');
      setIconSvgContent('');
      setIconSvgViewBox('0 0 24 24');
      setIconSvgFill('none');
      setIconSvgStroke('currentColor');
      setIconSvgStrokeWidth('2');

      showToast(`Restored default for ${selectedIconKey}`, 'success');
    } catch (err: any) {
      console.error('[ResetIconOverride] error:', err);
      showToast('Failed to reset icon.', 'error');
    } finally {
      setSavingIcon(false);
    }
  };

  // Keypress listener for Tab support inside editor textarea
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;

      // Insert 2 spaces for tab
      const newValue = value.substring(0, start) + '  ' + value.substring(end);
      setEditorCode(newValue);
      setHasChanges(true);

      // Reset selection point
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };

  // Compile Success / Failure callbacks from Sandbox compiler
  const handleCompileSuccess = () => {
    setCompileStatus('success');
    setCompileErrorMessage(null);
  };

  const handleCompileError = (err: string) => {
    setCompileStatus('error');
    setCompileErrorMessage(err);
  };

  // Trigger test draft: Save current code to Draft MongoDB collection
  const handleTestDraft = async () => {
    if (!currentUser) return;
    try {
      setSavingSandbox(true);
      setCompileStatus('compiling');

      const res = await api.post(
        '/admin/master-hand/sandbox',
        {
          componentId: selectedCompId,
          code: editorCode,
          adminUsername: currentUser
        },
        {
          headers: { 'x-admin-username': currentUser }
        }
      );

      if (res.data.success) {
        setSandboxCode(editorCode);
        setHasChanges(true);
        showToast('Sandbox draft updated! Compiling...', 'info');
      } else {
        showToast(res.data.error || 'Failed to save sandbox draft', 'error');
        setCompileStatus('error');
      }
    } catch (err: any) {
      console.error('[MasterHand] save sandbox draft error:', err);
      showToast(err.response?.data?.error || 'Failed to connect to API', 'error');
      setCompileStatus('error');
    } finally {
      setSavingSandbox(false);
    }
  };

  // Trigger deploy: Write current sandbox draft live to disk
  const handleDeployLive = async () => {
    const comp = components.find((c) => c.id === selectedCompId);
    if (!comp || !currentUser) return;

    if (!window.confirm(`Are you absolutely sure you want to deploy ${comp.filename} live? This will instantly compile on the server and update all active users.`)) {
      return;
    }

    try {
      setDeploying(true);

      // Perform a brace check first
      const openBraces = (editorCode.match(/\{/g) || []).length;
      const closeBraces = (editorCode.match(/\}/g) || []).length;
      if (Math.abs(openBraces - closeBraces) > 10) {
        showToast(`Deploy Blocked: highly unbalanced curly braces ({: ${openBraces}, }: ${closeBraces})`, 'error');
        return;
      }

      const res = await api.post(
        '/admin/master-hand/deploy',
        {
          componentId: selectedCompId,
          filename: comp.filename,
          code: editorCode,
          adminUsername: currentUser
        },
        {
          headers: { 'x-admin-username': currentUser }
        }
      );

      if (res.data.success) {
        showToast(`Success! ${comp.name} is now deployed live for all users.`, 'success');
        setHasChanges(false);
        // Refresh component catalog
        await fetchComponents();
      } else {
        showToast(res.data.error || 'Failed to deploy component live.', 'error');
      }
    } catch (err: any) {
      console.error('[MasterHand] deploy live error:', err);
      showToast(err.response?.data?.error || 'Failed to deploy component live.', 'error');
    } finally {
      setDeploying(false);
    }
  };

  // Discard local edits and clean sandbox draft
  const handleDiscardChanges = async () => {
    const comp = components.find((c) => c.id === selectedCompId);
    if (!comp || !currentUser) return;

    if (!window.confirm('Discard all local edits and restore live production code?')) {
      return;
    }

    try {
      setDiscarding(true);
      // Simply overwrite current editor states with the original file code
      setEditorCode(comp.code || '');
      setSandboxCode(comp.code || '');
      setHasChanges(false);
      setCompileStatus('idle');
      setCompileErrorMessage(null);

      // Delete draft on database by setting empty draft code or calling clean endpoint.
      // Since we don't have a direct clean endpoint, we can deploy the original code or overwrite.
      // Wait, we can deploy the original code back to the server to delete the database draft!
      const res = await api.post(
        '/admin/master-hand/deploy',
        {
          componentId: selectedCompId,
          filename: comp.filename,
          code: comp.code || '',
          adminUsername: currentUser
        },
        {
          headers: { 'x-admin-username': currentUser }
        }
      );

      if (res.data.success) {
        showToast('Draft discarded successfully. Restored live code.', 'success');
        await fetchComponents();
      }
    } catch (err: any) {
      console.error('[MasterHand] discard error:', err);
      showToast('Local states reset. Failed to clear database draft.', 'info');
    } finally {
      setDiscarding(false);
    }
  };

  // Toggle active mod in visual layout builder
  const toggleModActive = (modId: string) => {
    let nextMods;
    if (activeMods.includes(modId)) {
      nextMods = activeMods.filter(m => m !== modId);
    } else {
      nextMods = [...activeMods, modId];
    }
    setActiveMods(nextMods);
  };

  // Move mod item up in layout list
  const moveModUp = (index: number) => {
    if (index === 0) return;
    const nextMods = [...activeMods];
    const temp = nextMods[index];
    nextMods[index] = nextMods[index - 1];
    nextMods[index - 1] = temp;
    setActiveMods(nextMods);
  };

  // Move mod item down in layout list
  const moveModDown = (index: number) => {
    if (index === activeMods.length - 1) return;
    const nextMods = [...activeMods];
    const temp = nextMods[index];
    nextMods[index] = nextMods[index + 1];
    nextMods[index + 1] = temp;
    setActiveMods(nextMods);
  };

  // Apply layout changes live to App
  const handleApplyLayoutLive = async () => {
    if (!currentUser) return;
    try {
      setSavingLayout(true);

      // Update in user active customizations mods array
      const activeCustomizations = {
        ...(usersData[currentUser]?.activeCustomizations || {}),
        mods: activeMods
      };

      updateUserData((prev: any) => ({
        ...prev,
        [currentUser]: {
          ...prev[currentUser],
          activeCustomizations
        }
      }));

      // Call server to persist
      if (!isDemo) {
        await api.put(`/users/${encodeURIComponent(currentUser)}`, {
          activeCustomizations
        });
      }

      showToast('Visual Layout applied live successfully!', 'success');
    } catch (err: any) {
      console.error('[MasterHand] apply layout error:', err);
      showToast(err.response?.data?.error || 'Failed to save visual layout', 'error');
    } finally {
      setSavingLayout(false);
    }
  };

  // Helper mapping component ID to nice icons
  const getCompIcon = (compId: string) => {
    switch (compId) {
      case 'todo': return <Icons.CheckmarkIcon className="w-5 h-5 text-indigo-400" />;
      case 'vibeman1_footprint': return <Icons.TrophyIcon className="w-5 h-5 text-yellow-400" />;
      case 'pimp_my_ride': return <Icons.SettingsIcon className="w-5 h-5 text-pink-400" />;
      case 'art_class': return <Icons.SparklesIcon className="w-5 h-5 text-cyan-400" />;
      default: return <Icons.UserIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  const getLineNumbers = () => {
    const totalLines = editorCode.split('\n').length;
    return Array.from({ length: totalLines }, (_, idx) => idx + 1);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#06060c] text-white flex flex-col font-sans select-none overflow-hidden">
      {/* Premium Glassmorphic Header */}
      <header className="flex flex-col md:flex-row gap-4 md:gap-0 justify-between items-center px-4 py-3 md:px-8 md:py-5 bg-white/5 border-b border-white/10 backdrop-blur-xl shrink-0">
        <div className="flex items-center justify-between w-full md:w-auto gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={onBack}
              className="p-2 bg-white/5 hover:bg-white/10 rounded-xl border border-white/5 transition-all active:scale-95 text-indigo-300"
              title="Back to App"
            >
              <Icons.ChevronLeftIcon className="w-5 h-5" />
            </button>

            {isMobile && (
              <button
                onClick={() => setSidebarOpen(prev => !prev)}
                className={`p-2 rounded-xl border transition-all active:scale-95 flex items-center justify-center ${sidebarOpen
                    ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300 shadow-lg'
                    : 'bg-white/5 border-white/5 text-indigo-300/70 hover:bg-white/10'
                  }`}
                title="Toggle Catalog Menu"
              >
                <Icons.FilterIcon className="w-5 h-5" />
              </button>
            )}

            <div>
              <div className="flex items-center gap-1.5">
                <Icons.SparklesIcon className="w-4 h-4 text-indigo-400 animate-pulse" />
                <h1 className="text-sm md:text-base font-black uppercase tracking-widest bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 bg-clip-text text-transparent">
                  Master Hand
                </h1>
              </div>
              <p className="hidden md:block text-[9px] uppercase font-black tracking-widest text-white/40 italic">
                Live In-App Admin Builder & Code Studio
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isMobile && activeTab !== 'icons' && (
              <button
                onClick={() => setRightPanelOpen(prev => !prev)}
                className={`p-2 rounded-xl border transition-all active:scale-95 flex items-center justify-center ${rightPanelOpen
                    ? 'bg-purple-500/20 border-purple-500/40 text-purple-300 shadow-lg'
                    : 'bg-white/5 border-white/5 text-purple-300/70 hover:bg-white/10'
                  }`}
                title={activeTab === 'visual' ? "Toggle Inspector" : "Toggle Live Preview"}
              >
                {activeTab === 'visual' ? (
                  <Icons.SettingsIcon className="w-5 h-5" />
                ) : (
                  <Icons.PlayIcon className="w-5 h-5" />
                )}
              </button>
            )}

            <div className="md:hidden flex items-center gap-2 bg-white/5 border border-white/10 px-2.5 py-1.5 rounded-xl">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[8px] font-black uppercase tracking-widest text-white/70 italic">
                {currentUser}
              </span>
            </div>
          </div>
        </div>

        {/* Studio Tab Selection */}
        <div className="flex bg-black/40 p-1 rounded-2xl border border-white/5 shadow-2xl max-w-full overflow-x-auto">
          <button
            onClick={() => {
              setActiveTab('visual');
              if (isMobile) {
                setSidebarOpen(false);
                setRightPanelOpen(false);
              }
            }}
            className={`flex items-center gap-1.5 px-3 py-2 md:px-5 md:py-2.5 rounded-xl text-[10px] md:text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap ${activeTab === 'visual'
                ? 'bg-gradient-to-r from-indigo-500/20 to-purple-600/20 text-indigo-300 border border-indigo-500/20 shadow-lg'
                : 'text-white/60 hover:text-white'
              }`}
          >
            <Icons.UsersIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
            <span className="hidden sm:inline">Visual Layout Builder</span>
            <span className="sm:hidden">Visual</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('code');
              if (isMobile) {
                setSidebarOpen(false);
                setRightPanelOpen(false);
              }
            }}
            className={`flex items-center gap-1.5 px-3 py-2 md:px-5 md:py-2.5 rounded-xl text-[10px] md:text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap ${activeTab === 'code'
                ? 'bg-gradient-to-r from-indigo-500/20 to-purple-600/20 text-indigo-300 border border-indigo-500/20 shadow-lg'
                : 'text-white/60 hover:text-white'
              }`}
          >
            <Icons.SettingsIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
            <span className="hidden sm:inline">Live Code Studio</span>
            <span className="sm:hidden">Code</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('icons');
              if (isMobile) {
                setSidebarOpen(false);
                setRightPanelOpen(false);
              }
            }}
            className={`flex items-center gap-1.5 px-3 py-2 md:px-5 md:py-2.5 rounded-xl text-[10px] md:text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap ${activeTab === 'icons'
                ? 'bg-gradient-to-r from-indigo-500/20 to-purple-600/20 text-indigo-300 border border-indigo-500/20 shadow-lg'
                : 'text-white/60 hover:text-white'
              }`}
          >
            <Icons.SparklesIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
            <span className="hidden sm:inline">System Icons</span>
            <span className="sm:hidden">Icons</span>
          </button>
        </div>

        {/* Admin status tag */}
        <div className="hidden md:flex items-center gap-3 bg-white/5 border border-white/10 px-4 py-2 rounded-2xl">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.6)]" />
          <span className="text-[10px] font-black uppercase tracking-widest text-white/70 italic">
            Admin: {currentUser}
          </span>
        </div>
      </header>

      {/* Main Workspace Frame */}
      {loading ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <Icons.RotateIcon className="w-10 h-10 text-indigo-500 animate-spin" />
          <span className="text-xs uppercase font-black tracking-widest text-indigo-400 animate-pulse">
            Loading Architect Studio...
          </span>
        </div>
      ) : error ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 max-w-md mx-auto text-center px-4">
          <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500 font-bold">
            !
          </div>
          <h2 className="text-lg font-black uppercase tracking-widest text-red-400">Architect Loader Exception</h2>
          <p className="text-sm text-gray-400">{error}</p>
          <button
            onClick={fetchComponents}
            className="px-6 py-3 bg-white/5 hover:bg-white/10 rounded-2xl border border-white/5 text-xs font-black uppercase tracking-widest transition-all"
          >
            Retry Connection
          </button>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">

          {/* TAB 1: VISUAL LAYOUT BUILDER */}
          {activeTab === 'visual' && (
            <div className="flex-1 flex overflow-hidden animate-fade-in relative">
              {isMobile && sidebarOpen && (
                <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
              )}
              {isMobile && rightPanelOpen && (
                <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" onClick={() => setRightPanelOpen(false)} />
              )}

              {/* Left pane - Component Palette */}
              <div className={`border-r border-white/10 bg-[#080810]/95 flex flex-col justify-start flex-shrink-0 transition-transform duration-300 overflow-y-auto ${isMobile
                  ? 'fixed inset-y-0 left-0 z-40 w-80 pt-24 pb-6 px-6 shadow-2xl bg-[#080810]'
                  : 'w-80 p-6 bg-[#080810]/50'
                } ${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}`}>
                <div className="mb-6">
                  <h2 className="text-sm font-black uppercase tracking-widest text-indigo-400">
                    Component Palette
                  </h2>
                  <p className="text-[9px] uppercase font-bold tracking-widest text-white/40 mt-1">
                    Select mods to include in the home layout
                  </p>
                </div>

                <div className="space-y-4">
                  {components.map((comp) => {
                    const isActive = activeMods.includes(comp.id);
                    return (
                      <div
                        key={comp.id}
                        onClick={() => {
                          toggleModActive(comp.id);
                          if (isMobile) setSidebarOpen(false);
                        }}
                        className={`p-4 rounded-3xl border transition-all cursor-pointer hover:scale-[1.02] flex items-start gap-4 ${isActive
                            ? 'bg-gradient-to-r from-indigo-500/10 to-purple-600/10 border-indigo-500/40 shadow-lg shadow-indigo-500/5'
                            : 'bg-white/5 border-white/5 opacity-70 hover:opacity-100'
                          }`}
                      >
                        <div className="p-2.5 bg-black/40 rounded-xl shrink-0 mt-0.5 border border-white/5">
                          {getCompIcon(comp.id)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="text-xs font-black uppercase tracking-wide truncate">
                              {comp.name}
                            </h3>
                            {isActive && (
                              <div className="w-4 h-4 bg-indigo-500 rounded-full flex items-center justify-center">
                                <Icons.CheckmarkIcon className="w-2.5 h-2.5 text-white stroke-[3px]" />
                              </div>
                            )}
                          </div>
                          <p className="text-[9px] text-white/50 leading-tight mt-1 line-clamp-2">
                            {comp.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Grid controls or styling */}
                <div className="mt-8 border-t border-white/5 pt-6 space-y-6">
                  <div>
                    <h2 className="text-xs font-black uppercase tracking-widest text-purple-400 mb-3">
                      Theme Enforcer
                    </h2>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full border border-white/20 relative shadow-lg cursor-pointer overflow-hidden flex-shrink-0"
                        style={{ backgroundColor: layoutThemeColor }}
                      >
                        <input
                          type="color"
                          value={layoutThemeColor}
                          onChange={(e) => setLayoutThemeColor(e.target.value)}
                          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        />
                      </div>
                      <input
                        type="text"
                        value={layoutThemeColor}
                        onChange={(e) => setLayoutThemeColor(e.target.value)}
                        className="flex-1 min-w-0 bg-black/20 border border-white/5 rounded-xl py-2 px-4 text-xs font-mono"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleApplyLayoutLive}
                    disabled={savingLayout}
                    className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:opacity-50 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2"
                  >
                    {savingLayout ? 'Applying...' : 'Apply Layout Live 🚀'}
                  </button>
                </div>
              </div>

              {/* Center pane - Mobile Canvas Frame Mockup */}
              <div className={`flex-1 bg-[#050508] flex items-center justify-center overflow-y-auto ${isMobile ? 'p-2' : 'p-8'}`}>
                <div className="relative w-[340px] h-[640px] rounded-[50px] border-4 border-white/10 bg-[#0d0d15] shadow-2xl overflow-hidden flex flex-col justify-start">

                  {/* Phone Speaker & Camera Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-50 flex items-center justify-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-900/50" />
                    <div className="w-12 h-1 bg-white/10 rounded-full" />
                  </div>

                  {/* Phone Header mockup */}
                  <div className="pt-8 px-6 pb-3 flex justify-between items-center bg-[#0d0d15]/80 border-b border-white/5 shrink-0">
                    <span className="text-[10px] font-bold text-white/50 tracking-wider">GoodLyfe App</span>
                    <div className="flex gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                      <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                      <div className="w-3 h-1.5 rounded-full bg-indigo-500/80" />
                    </div>
                  </div>

                  {/* Dynamic Canvas Container */}
                  <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                    {activeMods.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center p-6 text-white/30 gap-3">
                        <Icons.SparklesIcon className="w-8 h-8 opacity-50" />
                        <span className="text-[10px] font-black uppercase tracking-widest leading-normal">
                          Canvas Empty.<br />Toggle mods in sidebar to assemble layout.
                        </span>
                      </div>
                    ) : (
                      activeMods.map((modId, index) => {
                        const comp = components.find(c => c.id === modId);
                        const isSelected = selectedLayoutMod === modId;
                        return (
                          <div
                            key={modId}
                            onClick={() => {
                              setSelectedLayoutMod(modId);
                              if (isMobile) setRightPanelOpen(true);
                            }}
                            className={`relative rounded-3xl p-4 transition-all cursor-pointer border ${isSelected
                                ? 'border-indigo-500 bg-white/5 shadow-2xl'
                                : 'border-white/5 bg-white/2 hover:border-white/10'
                              }`}
                          >
                            {/* Layout preview mock representation */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {getCompIcon(modId)}
                                <span className="text-[10px] font-black uppercase tracking-wider">
                                  {comp?.name || modId}
                                </span>
                              </div>

                              {/* Ordering Actions */}
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); moveModUp(index); }}
                                  disabled={index === 0}
                                  className="p-1 rounded bg-black/40 hover:bg-black/80 disabled:opacity-30 transition-all text-white"
                                >
                                  <Icons.ArrowUpIcon className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); moveModDown(index); }}
                                  disabled={index === activeMods.length - 1}
                                  className="p-1 rounded bg-black/40 hover:bg-black/80 disabled:opacity-30 transition-all text-white rotate-180"
                                >
                                  <Icons.ArrowUpIcon className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleModActive(modId); }}
                                  className="p-1 rounded bg-red-500/10 hover:bg-red-500/30 text-red-400 transition-all"
                                >
                                  <Icons.TrashIcon className="w-3 h-3" />
                                </button>
                              </div>
                            </div>

                            {/* Simplified Beautiful Mock Widget representation */}
                            {modId === 'todo' && (
                              <div className="space-y-1.5 p-1">
                                <div className="flex items-center gap-2 p-2 bg-black/30 rounded-xl border border-white/5">
                                  <div className="w-3 h-3 rounded-full border border-indigo-500 flex items-center justify-center shrink-0">
                                    <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                                  </div>
                                  <span className="text-[9px] text-white/80 line-through">Sandbox Compile Complete</span>
                                </div>
                                <div className="flex items-center gap-2 p-2 bg-black/30 rounded-xl border border-white/5 opacity-50">
                                  <div className="w-3 h-3 rounded-full border border-white/30 shrink-0" />
                                  <span className="text-[9px] text-white/80">Refactor Modular Layouts</span>
                                </div>
                              </div>
                            )}

                            {modId === 'vibeman1_footprint' && (
                              <div className="p-2 bg-black/30 rounded-2xl border border-white/5 flex gap-2 items-center">
                                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 flex items-center justify-center text-xs">
                                  🏆
                                </div>
                                <div>
                                  <p className="text-[9px] font-black uppercase text-yellow-400 tracking-wider">Lyfe Streak: 5 Days</p>
                                  <p className="text-[7px] text-white/50 uppercase mt-0.5">1,337 total impact points</p>
                                </div>
                              </div>
                            )}

                            {modId === 'pimp_my_ride' && (
                              <div className="p-2 bg-black/30 rounded-2xl border border-white/5 flex justify-between items-center">
                                <span className="text-[9px] uppercase tracking-wider text-pink-400 font-bold">GoodieStore Active</span>
                                <div className="px-2.5 py-1 bg-pink-500/20 border border-pink-500/30 rounded-lg text-[7px] font-black text-pink-300 uppercase">
                                  Shop Mods
                                </div>
                              </div>
                            )}

                            {modId === 'art_class' && (
                              <div className="p-2 bg-black/30 rounded-2xl border border-white/5 text-center">
                                <div className="w-full h-12 rounded-xl bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-white/5 flex items-center justify-center text-[8px] font-black uppercase text-indigo-300">
                                  🎨 Custom Sticker Editor
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Phone Bottom bar mockup */}
                  <div className="py-4 bg-[#0d0d15] border-t border-white/5 flex justify-center items-center shrink-0">
                    <div className="w-24 h-1 bg-white/20 rounded-full" />
                  </div>
                </div>
              </div>

              {/* Right pane - Properties Inspector */}
              <div className={`border-l border-white/10 bg-[#080810]/95 flex flex-col justify-start flex-shrink-0 transition-transform duration-300 overflow-y-auto ${isMobile
                  ? 'fixed inset-y-0 right-0 z-40 w-80 pt-24 pb-6 px-6 shadow-2xl bg-[#080810]'
                  : 'w-80 p-6 bg-[#080810]/50'
                } ${isMobile && !rightPanelOpen ? 'translate-x-full' : 'translate-x-0'}`}>
                <div className="mb-6">
                  <h2 className="text-sm font-black uppercase tracking-widest text-purple-400">
                    Properties Inspector
                  </h2>
                  <p className="text-[9px] uppercase font-bold tracking-widest text-white/40 mt-1">
                    Select a component on canvas to inspect
                  </p>
                </div>

                {selectedLayoutMod ? (
                  (() => {
                    const comp = components.find(c => c.id === selectedLayoutMod);
                    return (
                      <div className="space-y-6">
                        <div className="p-4 rounded-3xl bg-white/5 border border-white/10">
                          <div className="flex items-center gap-2.5 mb-2">
                            {getCompIcon(selectedLayoutMod)}
                            <h3 className="text-xs font-black uppercase tracking-widest text-indigo-300">
                              {comp?.name}
                            </h3>
                          </div>
                          <p className="text-[8px] font-mono text-white/40 truncate">
                            File: {comp?.filename}
                          </p>
                          <p className="text-xs text-white/70 leading-relaxed mt-3">
                            {comp?.description}
                          </p>
                        </div>

                        {/* Editable parameter mockup */}
                        <div className="space-y-4">
                          <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                            Custom parameters
                          </h4>

                          <div className="space-y-2">
                            <label className="text-[9px] font-black uppercase tracking-wide text-white/60">Widget accent glow</label>
                            <select className="w-full bg-black/30 border border-white/5 rounded-xl py-3 px-4 text-xs font-bold focus:border-indigo-500 outline-none">
                              <option value="indigo">Indigo Spark</option>
                              <option value="purple">Cosmic Purple</option>
                              <option value="pink">Hot Pink</option>
                              <option value="gold">Aurum Gold</option>
                            </select>
                          </div>

                          <div className="space-y-2">
                            <label className="text-[9px] font-black uppercase tracking-wide text-white/60">Widget scale</label>
                            <input type="range" min="80" max="120" defaultValue="100" className="w-full accent-indigo-500 h-1.5 bg-black/40 rounded-lg cursor-pointer" />
                          </div>

                          <div className="space-y-2">
                            <label className="text-[9px] font-black uppercase tracking-wide text-white/60">Header title alias</label>
                            <input type="text" placeholder={comp?.name} className="w-full bg-black/30 border border-white/5 rounded-xl py-2 px-4 text-xs focus:border-indigo-500 outline-none" />
                          </div>
                        </div>

                        <div className="pt-6 border-t border-white/5">
                          <button
                            onClick={() => {
                              toggleModActive(selectedLayoutMod);
                              if (isMobile) setRightPanelOpen(false);
                            }}
                            className="w-full py-3.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl text-[9px] font-black uppercase tracking-widest border border-red-500/20 transition-all flex items-center justify-center gap-2"
                          >
                            <Icons.TrashIcon className="w-3.5 h-3.5" />
                            Remove from Layout
                          </button>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-6 text-white/20 gap-3 border border-dashed border-white/5 rounded-3xl">
                    <span className="animate-spin" style={{ animationDuration: '6s' }}>
                      <Icons.SettingsIcon className="w-6 h-6 opacity-30" />
                    </span>
                    <span className="text-[9px] font-black uppercase tracking-widest leading-normal">
                      No Component Selected.<br />Click on any mockup block to adjust parameters.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: LIVE CODE STUDIO */}
          {activeTab === 'code' && (
            <div className="flex-1 flex overflow-hidden animate-fade-in relative">
              {isMobile && sidebarOpen && (
                <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
              )}
              {isMobile && rightPanelOpen && (
                <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" onClick={() => setRightPanelOpen(false)} />
              )}

              {/* Left Column Sidebar - Components code select */}
              <div className={`border-r border-white/10 bg-[#080810]/95 flex flex-col justify-start flex-shrink-0 transition-transform duration-300 overflow-y-auto ${isMobile
                  ? 'fixed inset-y-0 left-0 z-40 w-80 pt-24 pb-6 px-6 shadow-2xl bg-[#080810]'
                  : 'w-72 p-6 bg-[#080810]/50'
                } ${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}`}>
                <div className="mb-6">
                  <h2 className="text-sm font-black uppercase tracking-widest text-indigo-400">
                    Source Directory
                  </h2>
                  <p className="text-[9px] uppercase font-bold tracking-widest text-white/40 mt-1">
                    Select a component to inspect & edit code
                  </p>
                </div>

                <div className="space-y-3">
                  {components.map((comp) => {
                    const isSelected = comp.id === selectedCompId;
                    return (
                      <div
                        key={comp.id}
                        onClick={() => {
                          setSelectedCompId(comp.id);
                          if (isMobile) setSidebarOpen(false);
                        }}
                        className={`p-3.5 rounded-2xl border transition-all cursor-pointer ${isSelected
                            ? 'bg-gradient-to-r from-indigo-500/10 to-purple-600/10 border-indigo-500/40 shadow-lg shadow-indigo-500/5'
                            : 'bg-white/5 border-white/5 opacity-70 hover:opacity-100'
                          }`}
                      >
                        <div className="flex items-center gap-2.5">
                          {getCompIcon(comp.id)}
                          <span className="text-xs font-black uppercase tracking-wider truncate">
                            {comp.name}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2.5">
                          <span className="text-[8px] font-mono text-white/40">
                            {comp.filename}
                          </span>
                          {comp.draft && (
                            <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 border border-yellow-500/30 text-[7px] font-black uppercase tracking-wider text-yellow-400">
                              Draft active
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Middle Workspace Column - Code Editor */}
              <div className="flex-1 flex flex-col min-w-0 bg-[#040409]">
                {/* Editor Status Bar */}
                <div className="px-6 py-4 bg-white/2 border-b border-white/5 flex justify-between items-center shrink-0">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono bg-black/40 border border-white/10 px-3 py-1.5 rounded-lg text-gray-300">
                      src/components/{components.find((c) => c.id === selectedCompId)?.filename}
                    </span>
                    {hasChanges && (
                      <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-[8px] font-black uppercase tracking-widest text-yellow-400 animate-pulse">
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                        Unsaved draft
                      </span>
                    )}
                  </div>

                  {/* Compile status indicator */}
                  <div className="flex items-center gap-2.5">
                    <span className="text-[9px] font-black uppercase tracking-widest text-white/40">
                      Sandbox State:
                    </span>
                    {compileStatus === 'idle' && (
                      <span className="text-[9px] font-black uppercase tracking-widest text-gray-500">
                        Idle
                      </span>
                    )}
                    {compileStatus === 'compiling' && (
                      <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-blue-400 animate-pulse">
                        <Icons.RotateIcon className="w-3 h-3 animate-spin" />
                        Compiling
                      </span>
                    )}
                    {compileStatus === 'success' && (
                      <span className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-400">
                        ✓ Compiles OK
                      </span>
                    )}
                    {compileStatus === 'error' && (
                      <span className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-red-400">
                        ✕ Compile Failure
                      </span>
                    )}
                  </div>
                </div>

                {/* Styled Space Editor with Line Numbers */}
                <div className="flex-1 flex overflow-hidden min-h-0 relative font-mono">
                  {/* Line Numbers Column */}
                  <div
                    ref={lineNumbersRef}
                    className="w-12 select-none text-right pr-3.5 py-4 border-r border-white/5 text-[11px] font-bold text-white/20 select-none overflow-y-hidden"
                    style={{ lineHeight: '1.75rem' }}
                  >
                    {getLineNumbers().map((num) => (
                      <div key={num} className="h-7">
                        {num}
                      </div>
                    ))}
                  </div>

                  {/* Textarea Area */}
                  <textarea
                    ref={textareaRef}
                    value={editorCode}
                    onChange={(e) => {
                      setEditorCode(e.target.value);
                      setHasChanges(true);
                      setCompileStatus('idle');
                    }}
                    onKeyDown={handleKeyDown}
                    onScroll={handleScroll}
                    className="flex-1 h-full bg-transparent text-gray-100 py-4 px-5 text-[11px] font-mono resize-none focus:outline-none overflow-auto border-none selection:bg-indigo-500/30 selection:text-white"
                    style={{
                      lineHeight: '1.75rem',
                      tabSize: 2,
                      whiteSpace: 'pre',
                      wordWrap: 'normal'
                    }}
                    spellCheck={false}
                    autoComplete="off"
                    autoCapitalize="off"
                  />
                </div>

                {/* Editor Action Buttons Footer */}
                <div className={`px-4 py-3 md:px-6 md:py-4 bg-white/2 border-t border-white/5 flex ${isMobile ? 'flex-col gap-3' : 'justify-between items-center'
                  } shrink-0`}>
                  <div className={`flex ${isMobile ? 'w-full' : 'gap-3'}`}>
                    <button
                      onClick={handleDiscardChanges}
                      disabled={discarding || deploying}
                      className={`py-3 bg-red-600/10 hover:bg-red-600/20 disabled:opacity-50 text-red-400 rounded-xl font-black uppercase tracking-widest border border-red-500/10 transition-all flex items-center justify-center gap-2 ${isMobile ? 'w-full text-[9px] px-3' : 'text-[10px] px-5'
                        }`}
                    >
                      <Icons.TrashIcon className="w-3.5 h-3.5" />
                      Discard Edits
                    </button>
                  </div>

                  <div className={`flex ${isMobile ? 'flex-col w-full gap-2' : 'gap-3'}`}>
                    <button
                      onClick={handleTestDraft}
                      disabled={savingSandbox || deploying}
                      className={`py-3 bg-indigo-600/20 hover:bg-indigo-600/40 disabled:opacity-50 text-indigo-300 rounded-xl font-black uppercase tracking-widest border border-indigo-500/30 transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/5 ${isMobile ? 'w-full text-[9px] px-3' : 'text-[10px] px-6'
                        }`}
                    >
                      {savingSandbox ? 'Transpiling...' : 'Test Draft in Sandbox'}
                    </button>
                    <button
                      onClick={handleDeployLive}
                      disabled={deploying}
                      className={`py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:opacity-50 text-white rounded-xl font-black uppercase tracking-widest transition-all shadow-lg shadow-indigo-500/25 flex items-center justify-center gap-2 ${isMobile ? 'w-full text-[9px]' : 'text-[10px] px-6'
                        }`}
                    >
                      {deploying ? 'Deploying Live...' : 'Deploy Live 🚀'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column - Live Interactive Sandbox Preview */}
              <div className={`border-l border-white/10 bg-[#06060a] flex flex-col shrink-0 transition-transform duration-300 overflow-hidden ${isMobile
                  ? 'fixed inset-y-0 right-0 z-40 w-80 pt-24 pb-6 shadow-2xl bg-[#06060a]'
                  : 'w-[450px]'
                } ${isMobile && !rightPanelOpen ? 'translate-x-full' : 'translate-x-0'}`}>
                <div className="px-6 py-4.5 bg-white/2 border-b border-white/5 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 animate-ping" />
                    <h2 className="text-xs font-black uppercase tracking-widest text-indigo-400">
                      Live Sandbox Preview
                    </h2>
                  </div>
                  <span className="text-[8px] bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded uppercase font-bold tracking-wider">
                    Isolated Shell
                  </span>
                </div>

                {/* Sandbox container frame */}
                <div className="flex-1 overflow-y-auto bg-black/40 flex items-center justify-center">
                  <MasterHandSandbox
                    key={sandboxCode}
                    componentId={selectedCompId}
                    code={sandboxCode}
                    adminUsername={currentUser || ''}
                    onCompileSuccess={handleCompileSuccess}
                    onCompileError={handleCompileError}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: SYSTEM ICONS CUSTOMIZER */}
          {activeTab === 'icons' && (
            <div className="flex-1 flex overflow-hidden animate-fade-in relative">
              {isMobile && sidebarOpen && (
                <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
              )}

              {/* Left Column: Icons Search & List Grid */}
              <div className={`border-r border-white/10 bg-[#080810]/95 flex flex-col justify-start flex-shrink-0 transition-transform duration-300 overflow-hidden ${isMobile
                  ? 'fixed inset-y-0 left-0 z-40 w-80 pt-24 pb-6 px-6 shadow-2xl bg-[#080810]'
                  : 'w-96 p-6 bg-[#080810]/50'
                } ${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}`}>
                <div className="mb-4 shrink-0">
                  <h2 className="text-sm font-black uppercase tracking-widest text-indigo-400">
                    System Icons Catalog
                  </h2>
                  <p className="text-[9px] uppercase font-bold tracking-widest text-white/40 mt-1">
                    Select a core interface icon to customize system-wide
                  </p>
                </div>

                {/* Catalog Search Bar */}
                <div className="relative flex items-center mb-4 shrink-0">
                  <span className="absolute left-3.5 text-white/30">
                    <Icons.SearchIcon className="w-4 h-4" />
                  </span>
                  <input
                    type="text"
                    placeholder="SEARCH ICONS CATALOG..."
                    value={iconSearch}
                    onChange={(e) => setIconSearch(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-xs font-bold uppercase tracking-wider text-white placeholder-white/30 focus:outline-none focus:border-indigo-500/50 transition-all"
                  />
                  {iconSearch && (
                    <button
                      onClick={() => setIconSearch('')}
                      className="absolute right-3.5 text-white/40 hover:text-white transition-all text-xs uppercase font-black"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Scrollable Icon Grid */}
                <div className="flex-1 overflow-y-auto pr-1 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {ALL_ICONS.filter(
                      (icon) =>
                        icon.name.toLowerCase().includes(iconSearch.toLowerCase()) ||
                        icon.key.toLowerCase().includes(iconSearch.toLowerCase())
                    ).map((icon) => {
                      const isSelected = selectedIconKey === icon.key;
                      const IconComponent = (Icons as any)[icon.key];
                      const systemData = usersData?.['goodlyfe'] || {};
                      const appIcons = systemData.appIcons || {};
                      const iconConfig = appIcons[icon.key];
                      const hasOverride = !!iconConfig;

                      return (
                        <div
                          key={icon.key}
                          onClick={() => {
                            setSelectedIconKey(icon.key);
                            if (isMobile) setSidebarOpen(false);
                          }}
                          className={`p-3 rounded-2xl border transition-all cursor-pointer hover:scale-[1.02] flex flex-col items-center justify-between text-center min-h-[110px] select-none ${isSelected
                              ? 'bg-gradient-to-br from-indigo-500/10 to-purple-600/10 border-indigo-500/50 shadow-lg shadow-indigo-500/5'
                              : 'bg-white/5 border-white/5 opacity-70 hover:opacity-100 hover:border-white/10'
                            }`}
                        >
                          <div className="flex-1 flex items-center justify-center min-h-[44px]">
                            {IconComponent ? (
                              <IconComponent className="w-7 h-7 text-indigo-300" />
                            ) : (
                              <span className="text-xs">?</span>
                            )}
                          </div>

                          <div className="w-full">
                            <span className="text-[9px] font-black uppercase tracking-wider block truncate px-1">
                              {icon.name}
                            </span>
                            <span className="text-[7px] font-mono text-white/30 block truncate">
                              {icon.key}
                            </span>
                          </div>

                          {hasOverride && (
                            <span className={`mt-1.5 px-2 py-0.5 rounded-full border text-[7px] font-black uppercase tracking-wider ${iconConfig.type === 'text'
                                ? 'bg-purple-500/15 border-purple-500/30 text-purple-300'
                                : iconConfig.type === 'media'
                                  ? 'bg-pink-500/15 border-pink-500/30 text-pink-300'
                                  : 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300'
                              }`}>
                              {iconConfig.type} override
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column: Customizer Inspector Panel */}
              <div className={`flex-1 bg-[#050508] flex flex-col justify-start overflow-y-auto ${isMobile ? 'p-4' : 'p-8'}`}>
                <div className="mb-6 border-b border-white/5 pb-4 shrink-0">
                  <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">
                    Customizer Inspector
                  </span>
                  <h2 className="text-2xl font-black uppercase tracking-wide bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent mt-1">
                    {ALL_ICONS.find((i) => i.key === selectedIconKey)?.name || selectedIconKey}
                  </h2>
                  <p className="text-[9px] font-mono text-white/40 uppercase mt-0.5">
                    Internal Identifier: {selectedIconKey}
                  </p>
                </div>

                {/* Beautiful large interactive preview area */}
                <div className="p-6 rounded-3xl bg-white/5 border border-white/10 flex flex-col items-center justify-center text-center mb-8 relative overflow-hidden group shrink-0">
                  <div className="absolute top-3 right-4 px-2 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/20 text-[7px] font-black text-indigo-300 uppercase tracking-widest">
                    Interactive Preview
                  </div>
                  <div className="w-20 h-20 rounded-2xl bg-black/40 border border-white/5 flex items-center justify-center shadow-2xl transition-transform group-hover:scale-105">
                    {(() => {
                      const IconComponent = (Icons as any)[selectedIconKey];
                      return IconComponent ? (
                        <IconComponent className="w-10 h-10 text-indigo-400" />
                      ) : null;
                    })()}
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-[0.15em] text-white/50 mt-4 leading-normal">
                    This is how it renders live across the interface.<br />Test it with clicks or active states.
                  </span>
                </div>

                {/* Customizer Tabs Selector */}
                <div className="mb-6 shrink-0">
                  <label className="text-[10px] font-black uppercase tracking-widest text-white/50 block mb-3">
                    CUSTOMIZATION METHOD
                  </label>
                  <div className="grid grid-cols-4 gap-1 p-1 bg-black/40 rounded-2xl border border-white/5">
                    {[
                      { type: 'default', label: isMobile ? 'Vector' : 'Default Vector' },
                      { type: 'text', label: isMobile ? 'Text' : 'Text / Emoji' },
                      { type: 'media', label: isMobile ? 'Media' : 'Media Override' },
                      { type: 'svg', label: isMobile ? 'SVG' : 'Raw SVG Code' }
                    ].map((tab) => (
                      <button
                        key={tab.type}
                        onClick={() => setIconOverrideType(tab.type as any)}
                        className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all text-center ${iconOverrideType === tab.type
                            ? 'bg-gradient-to-r from-indigo-600/20 to-purple-600/20 text-indigo-300 border border-indigo-500/20 shadow-lg'
                            : 'text-white/40 hover:text-white/80'
                          }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Edit Form Fields based on Type Selection */}
                <div className="flex-1 bg-white/2 border border-white/5 rounded-3xl p-6 mb-8 min-h-[160px] flex flex-col justify-center shrink-0">
                  {iconOverrideType === 'default' && (
                    <div className="text-center py-4 space-y-2">
                      <Icons.ShieldIcon className="w-8 h-8 text-white/20 mx-auto" />
                      <p className="text-xs font-bold uppercase tracking-wider text-white/70">
                        Default Vector Active
                      </p>
                      <p className="text-[9px] text-white/40 uppercase max-w-sm mx-auto leading-relaxed">
                        This icon is currently using its original, responsive vector artwork. Overriding it will dynamically replace it system-wide instantly.
                      </p>
                    </div>
                  )}

                  {iconOverrideType === 'text' && (
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-[10px] font-black uppercase tracking-wide text-white/60">
                            Custom Text / Emoji
                          </label>
                          <span className="text-[8px] font-mono text-white/30 uppercase">
                            {iconText.length}/4 CHARS MAX
                          </span>
                        </div>
                        <input
                          type="text"
                          value={iconText}
                          onChange={(e) => setIconText(e.target.value.substring(0, 4))}
                          placeholder="e.g. HOME, 🔥, LUV"
                          maxLength={4}
                          className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-xs font-black uppercase tracking-wider text-white focus:outline-none focus:border-indigo-500/50 transition-all"
                        />
                      </div>
                      <p className="text-[8px] text-white/40 uppercase leading-normal">
                        Tip: You can use standard letters, numbers, symbols, or emojis. Emojis and short text will automatically scale and fit inside active layouts.
                      </p>
                    </div>
                  )}

                  {iconOverrideType === 'media' && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black uppercase tracking-wide text-white/60">
                          Media Upload or Paste
                        </label>
                        <span className="text-[8px] font-mono text-white/30 uppercase">
                          MAX 5MB (IMAGE, GIF, VIDEO)
                        </span>
                      </div>

                      {uploadingIconMedia ? (
                        <div className="border border-dashed border-indigo-500/40 bg-indigo-500/5 rounded-3xl p-8 flex flex-col items-center justify-center gap-4 text-center">
                          <Icons.RotateIcon className="w-8 h-8 text-indigo-400 animate-spin" />
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-300">
                              Uploading Asset...
                            </p>
                            <div className="w-48 h-1.5 bg-black/40 rounded-full overflow-hidden mt-2">
                              <div
                                className="h-full bg-indigo-500 transition-all duration-150"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                            <p className="text-[8px] font-mono text-white/40 mt-1">
                              {uploadProgress}%
                            </p>
                          </div>
                        </div>
                      ) : iconMediaUrl ? (
                        (() => {
                          const previewUrl = resolveLocalUrl(iconMediaUrl);
                          return (
                            <div className="border border-white/10 bg-black/40 rounded-3xl p-4 flex items-center gap-4">
                              <div className="w-16 h-16 rounded-xl bg-black/20 border border-white/5 overflow-hidden flex items-center justify-center shrink-0">
                                {previewUrl.match(/\.(mp4|webm|ogg|mov)(?:\?|$)/i) ? (
                                  <video
                                    src={previewUrl}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <img
                                    src={previewUrl}
                                    className="w-full h-full object-cover"
                                    alt="Custom Icon Asset"
                                  />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[9px] font-black uppercase tracking-wide text-emerald-400">
                                  Media File Linked
                                </p>
                                <p className="text-[8px] font-mono text-white/40 truncate mt-0.5">
                                  {previewUrl}
                                </p>
                                <div className="flex gap-2 mt-2">
                                  <label className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-[8px] font-black uppercase tracking-wider text-white transition-all cursor-pointer">
                                    Replace File
                                    <input
                                      type="file"
                                      accept="image/*,video/*"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleIconMediaUpload(file);
                                      }}
                                      className="hidden"
                                    />
                                  </label>
                                  <button
                                    onClick={() => setIconMediaUrl('')}
                                    className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 rounded-lg text-[8px] font-black uppercase tracking-wider text-red-400 transition-all border border-red-500/10"
                                  >
                                    Clear
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <div
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={handleFileDrop}
                          onPaste={handlePaste}
                          tabIndex={0}
                          className="border border-dashed border-white/10 hover:border-indigo-500/40 bg-black/30 rounded-3xl p-6 text-center transition-all flex flex-col items-center justify-center gap-4 cursor-pointer group focus:outline-none focus:border-indigo-500/60"
                        >
                          <label className="cursor-pointer flex flex-col items-center gap-2 w-full">
                            <Icons.PaperclipIcon className="w-8 h-8 text-white/30 group-hover:text-indigo-400 transition-all" />
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-wide text-white/70">
                                Drag & Drop Asset
                              </p>
                              <p className="text-[8px] text-white/40 uppercase mt-1">
                                Click here to manually upload file under 5MB
                              </p>
                            </div>
                            <input
                              type="file"
                              accept="image/*,video/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleIconMediaUpload(file);
                              }}
                              className="hidden"
                            />
                          </label>
                          <div className="w-full h-px bg-white/5 my-1"></div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClipboardButtonPaste();
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 rounded-xl transition-all border border-indigo-500/10 text-[9px] font-black uppercase tracking-wider active:scale-95"
                          >
                            Paste from Clipboard
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {iconOverrideType === 'svg' && (
                    <div className="space-y-4">
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-wide text-white/60 block mb-2">
                          Raw SVG Inner Content (Paths / Shapes)
                        </label>
                        <textarea
                          value={iconSvgContent}
                          onChange={(e) => setIconSvgContent(e.target.value)}
                          placeholder="e.g. &lt;path d=&quot;M12 2L2 22h20L12 2z&quot; /&gt;"
                          className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-xs font-mono text-indigo-200 placeholder-white/20 focus:outline-none focus:border-indigo-500/50 resize-none h-24"
                          spellCheck={false}
                          autoComplete="off"
                        />
                      </div>

                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <label className="text-[8px] font-black uppercase tracking-wide text-white/40 block mb-1">
                            ViewBox
                          </label>
                          <input
                            type="text"
                            value={iconSvgViewBox}
                            onChange={(e) => setIconSvgViewBox(e.target.value)}
                            placeholder="0 0 24 24"
                            className="w-full bg-black/40 border border-white/10 rounded-lg py-2 px-3 text-[10px] font-mono text-center"
                          />
                        </div>
                        <div>
                          <label className="text-[8px] font-black uppercase tracking-wide text-white/40 block mb-1">
                            Fill
                          </label>
                          <input
                            type="text"
                            value={iconSvgFill}
                            onChange={(e) => setIconSvgFill(e.target.value)}
                            placeholder="none"
                            className="w-full bg-black/40 border border-white/10 rounded-lg py-2 px-3 text-[10px] font-mono text-center"
                          />
                        </div>
                        <div>
                          <label className="text-[8px] font-black uppercase tracking-wide text-white/40 block mb-1">
                            Stroke
                          </label>
                          <input
                            type="text"
                            value={iconSvgStroke}
                            onChange={(e) => setIconSvgStroke(e.target.value)}
                            placeholder="currentColor"
                            className="w-full bg-black/40 border border-white/10 rounded-lg py-2 px-3 text-[10px] font-mono text-center"
                          />
                        </div>
                        <div>
                          <label className="text-[8px] font-black uppercase tracking-wide text-white/40 block mb-1">
                            Stroke Width
                          </label>
                          <input
                            type="text"
                            value={iconSvgStrokeWidth}
                            onChange={(e) => setIconSvgStrokeWidth(e.target.value)}
                            placeholder="2"
                            className="w-full bg-black/40 border border-white/10 rounded-lg py-2 px-3 text-[10px] font-mono text-center"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Customizer Save Actions Footer */}
                <div className={`flex ${isMobile ? 'flex-col gap-3' : 'gap-4'} border-t border-white/5 pt-6 shrink-0`}>
                  <button
                    onClick={handleResetIconToDefault}
                    disabled={savingIcon}
                    className={`py-4 bg-red-600/10 hover:bg-red-600/20 border border-red-500/20 disabled:opacity-50 text-red-400 rounded-2xl font-black uppercase tracking-[0.15em] transition-all flex items-center justify-center gap-2 ${isMobile ? 'w-full text-[9px] py-3' : 'flex-1 text-[10px]'
                      }`}
                  >
                    <Icons.RotateIcon className="w-4 h-4" />
                    Restore Default
                  </button>

                  <button
                    onClick={handleSaveIconOverride}
                    disabled={savingIcon || (iconOverrideType === 'media' && uploadingIconMedia)}
                    className={`py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:opacity-50 text-white rounded-2xl font-black uppercase tracking-[0.15em] transition-all shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2 ${isMobile ? 'w-full text-[9px] py-3' : 'flex-[2] text-[10px]'
                      }`}
                  >
                    {savingIcon ? 'Saving Changes...' : 'Save Icon Override 🚀'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {/* Futuristic Floating Toast notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-[99] animate-fade-in">
          <div
            className={`px-5 py-3.5 rounded-2xl border backdrop-blur-xl shadow-2xl flex items-center gap-3 ${toast.type === 'success'
                ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'
                : toast.type === 'error'
                  ? 'bg-red-500/15 border-red-500/30 text-red-300'
                  : 'bg-indigo-500/15 border-indigo-500/30 text-indigo-300'
              }`}
          >
            {toast.type === 'success' && <Icons.CheckmarkIcon className="w-4 h-4 text-emerald-400" />}
            {toast.type === 'error' && <div className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center">!</div>}
            {toast.type === 'info' && <Icons.RotateIcon className="w-4 h-4 text-indigo-400 animate-spin" />}
            <span className="text-[10px] font-black uppercase tracking-widest italic">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};
